import React, { useState, useEffect } from 'react';
import { useAuth } from '@contexts/AuthContext';
import {
  BookOpen,
  Plus,
  Search,
  Edit,
  Trash2,
  RefreshCw,
  FileText,
  Map,
  AlertTriangle,
  Calendar,
  Tag,
  Eye,
  ChevronDown,
} from 'lucide-react';

import { guidanceAndProcessMapsApi } from '@domains/compliance/definition/guidance/guidanceAndProcessMaps.api';
import {
  GuidanceAndProcessMapsAdapter,
  type Guidance,
  type ProcessMap
} from '@domains/compliance/definition/guidance/guidanceAndProcessMaps.adapter';
import { notify } from '@core/notifications/notify';
import { useModal, ConfirmModal, GuidanceModal, ProcessMapModal, GuidancePreviewModal, ProcessMapPreviewModal } from '@components/modal';
import PageInfoButton from '@/components/PageInfoButton';

const getCategoryColor = (category: string) => {
  switch (category) {
    case 'compliance': return 'bg-blue-100 text-blue-700 border-blue-200';
    case 'process': return 'bg-purple-100 text-purple-700 border-purple-200';
    case 'documentation': return 'bg-emerald-100 text-emerald-700 border-emerald-200';
    case 'training': return 'bg-amber-100 text-amber-700 border-amber-200';
    default: return 'bg-slate-100 text-slate-700 border-slate-200';
  }
};

const GuidanceAndProcessMaps: React.FC = () => {
  const { hasPermission, isLoading: authLoading } = useAuth();
  const { openModal } = useModal();
  const [activeTab, setActiveTab] = useState<'guidance' | 'process-maps'>('guidance');
  const [guidanceItems, setGuidanceItems] = useState<Guidance[]>([]);
  const [processMaps, setProcessMaps] = useState<ProcessMap[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [expandedIds, setExpandedIds] = useState<Set<number>>(() => {
    try {
      const saved = localStorage.getItem('guidance_expanded');
      return saved ? new Set(JSON.parse(saved) as number[]) : new Set<number>();
    } catch { return new Set<number>(); }
  });

  useEffect(() => { loadData(); }, [activeTab]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      if (activeTab === 'guidance') await loadGuidance();
      else await loadProcessMaps();
    } finally {
      setIsLoading(false);
    }
  };

  const loadGuidance = async () => {
    try {
      const dtos = await guidanceAndProcessMapsApi.getGuidance();
      const items = GuidanceAndProcessMapsAdapter.adaptGuidances(dtos);
      setGuidanceItems(items);
      if (!localStorage.getItem('guidance_expanded') && items.length > 0) {
        const expanded = new Set([items[0].id]);
        setExpandedIds(expanded);
        localStorage.setItem('guidance_expanded', JSON.stringify(Array.from(expanded)));
      }
    } catch (error) {
      notify.error('Failed to load guidance items');
    }
  };

  const loadProcessMaps = async () => {
    try {
      const dtos = await guidanceAndProcessMapsApi.getProcessMaps();
      const items = GuidanceAndProcessMapsAdapter.adaptProcessMaps(dtos);
      setProcessMaps(items);
      if (!localStorage.getItem('pm_expanded') && items.length > 0) {
        const expanded = new Set([items[0].id]);
        setExpandedIds(expanded);
        localStorage.setItem('pm_expanded', JSON.stringify(Array.from(expanded)));
      }
    } catch (error) {
      notify.error('Failed to load process maps');
    }
  };

  const toggleExpand = (id: number) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      const key = activeTab === 'guidance' ? 'guidance_expanded' : 'pm_expanded';
      localStorage.setItem(key, JSON.stringify(Array.from(next)));
      return next;
    });
  };

  const filteredGuidance = guidanceItems.filter(item => {
    const matchesSearch = !searchTerm || item.title.toLowerCase().includes(searchTerm.toLowerCase()) || item.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !filterCategory || item.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  const filteredProcessMaps = processMaps.filter(item => {
    const matchesSearch = !searchTerm || item.title.toLowerCase().includes(searchTerm.toLowerCase()) || item.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !filterCategory || item.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  const handleSaveGuidance = async (data: Partial<Guidance>) => {
    if (!data.title || !data.content) { notify.error('Title and content are required'); return; }
    try {
      const payload = {
        title: data.title!, description: data.description || '', content: data.content!,
        category: data.category || 'general', framework_id: data.framework_id || null,
        scope_id: data.scope_id || null, section_id: data.section_id || null,
        page_id: data.page_id || null, route_path: (data as any).route_path || null,
        is_active: data.is_active !== undefined ? data.is_active : true,
      };
      if (data.id) await guidanceAndProcessMapsApi.updateGuidance(data.id, payload);
      else await guidanceAndProcessMapsApi.createGuidance(payload);
      notify.success('Guidance saved');
      loadGuidance();
    } catch (error) {
      notify.error('Failed to save guidance');
    }
  };

  const handleDeleteGuidance = (id: number) => {
    openModal({
      component: ConfirmModal,
      props: {
        title: 'Delete Guidance', message: 'Are you sure? This cannot be undone.',
        confirmText: 'Delete', cancelText: 'Cancel', type: 'danger',
        onConfirm: async () => { try { await guidanceAndProcessMapsApi.deleteGuidance(id); notify.success('Deleted'); loadGuidance(); } catch { notify.error('Failed to delete'); } },
        onCancel: () => {},
      },
    });
  };

  const handleSaveProcessMap = async (data: Partial<ProcessMap>) => {
    if (!data.title || !data.diagram_data) { notify.error('Title and diagram data are required'); return; }
    try {
      const payload = {
        title: data.title!, description: data.description || '', diagram_data: data.diagram_data!,
        category: data.category || 'process', framework_id: data.framework_id || null,
        is_active: data.is_active || true,
      };
      if (data.id) await guidanceAndProcessMapsApi.updateProcessMap(data.id, payload);
      else await guidanceAndProcessMapsApi.createProcessMap(payload);
      notify.success('Process map saved');
      loadProcessMaps();
    } catch (error) {
      notify.error('Failed to save process map');
    }
  };

  const handleDeleteProcessMap = (id: number) => {
    openModal({
      component: ConfirmModal,
      props: {
        title: 'Delete Process Map', message: 'Are you sure? This cannot be undone.',
        confirmText: 'Delete', cancelText: 'Cancel', type: 'danger',
        onConfirm: async () => { try { await guidanceAndProcessMapsApi.deleteProcessMap(id); notify.success('Deleted'); loadProcessMaps(); } catch { notify.error('Failed to delete'); } },
        onCancel: () => {},
      },
    });
  };

  if (authLoading || isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <RefreshCw className="text-blue-600 animate-spin" size={40} />
      </div>
    );
  }

  if (!hasPermission('templates.update')) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg border border-red-200 p-8 max-w-md">
          <AlertTriangle className="text-red-500 mb-4" size={48} />
          <h2 className="text-xl font-bold text-slate-800 mb-2">Access Denied</h2>
          <p className="text-slate-600">You don't have permission to access Guidance & Process Maps.</p>
        </div>
      </div>
    );
  }

  const currentItems = activeTab === 'guidance' ? filteredGuidance : filteredProcessMaps;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto px-6 py-6">
        {/* Page Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center gap-1"><h1 className="text-3xl font-bold text-slate-900 text-left">Guidance & Process Maps</h1><PageInfoButton /></div>
            <p className="text-slate-500 text-sm mt-0.5 text-left">Manage guidance content and process diagrams for compliance teams</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={loadData} className="inline-flex items-center gap-2 px-4 py-2.5 bg-white text-slate-700 font-medium text-sm rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors">
              <RefreshCw size={16} /> Refresh
            </button>
            <button
              onClick={() => {
                if (activeTab === 'guidance') {
                  openModal({ component: GuidanceModal, props: { onSubmit: handleSaveGuidance, selectedGuidance: null }, options: { size: 'large' } });
                } else {
                  openModal({ component: ProcessMapModal, props: { onSubmit: handleSaveProcessMap, selectedProcessMap: null }, options: { size: 'large' } });
                }
              }}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white font-medium text-sm rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
            >
              <Plus size={16} /> {activeTab === 'guidance' ? 'Add Guidance' : 'Add Process Map'}
            </button>
          </div>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-3xl border border-slate-100 p-4" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider text-left">Total Guidance</p>
            <p className="text-3xl font-bold text-slate-900 mt-1">{guidanceItems.length}</p>
          </div>
          <div className="bg-white rounded-3xl border border-slate-100 p-4" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider text-left">Total Process Maps</p>
            <p className="text-3xl font-bold text-slate-900 mt-1">{processMaps.length}</p>
          </div>
          <div className="bg-white rounded-xl border border-emerald-200 p-4 bg-emerald-50/50">
            <p className="text-xs font-medium text-emerald-700 uppercase tracking-wider text-left">Active</p>
            <p className="text-2xl font-bold text-emerald-800 mt-1">
              {activeTab === 'guidance'
                ? guidanceItems.filter(g => g.is_active).length
                : processMaps.filter(p => p.is_active).length}
            </p>
          </div>
          <div className="bg-white rounded-3xl border border-slate-100 p-4" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider text-left">Showing</p>
            <p className="text-3xl font-bold text-slate-900 mt-1">{currentItems.length}</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-white rounded-lg border border-slate-200 p-1 w-fit">
          <button
            onClick={() => { setActiveTab('guidance'); setSearchTerm(''); setFilterCategory(''); }}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'guidance' ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-50'
            }`}
          >
            <FileText size={16} /> Guidance
          </button>
          <button
            onClick={() => { setActiveTab('process-maps'); setSearchTerm(''); setFilterCategory(''); }}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'process-maps' ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-50'
            }`}
          >
            <Map size={16} /> Process Maps
          </button>
        </div>

        {/* Search + Filter */}
        <div className="flex items-center gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
            <input
              type="text"
              placeholder={`Search ${activeTab === 'guidance' ? 'guidance' : 'process maps'} by title or description...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 text-sm"
            />
          </div>
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="px-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400"
          >
            <option value="">All Categories</option>
            <option value="compliance">Compliance</option>
            <option value="process">Process</option>
            <option value="documentation">Documentation</option>
            <option value="training">Training</option>
          </select>
        </div>

        {/* Content List — collapsible rows */}
        {currentItems.length === 0 ? (
          <div className="bg-white rounded-3xl border border-slate-100 p-12">
            <div className="w-20 h-20 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
              {activeTab === 'guidance' ? <FileText className="text-blue-500" size={40} /> : <Map className="text-blue-500" size={40} />}
            </div>
            <h3 className="text-xl font-bold text-slate-800 mb-2">
              {searchTerm || filterCategory ? 'No Results' : `No ${activeTab === 'guidance' ? 'Guidance' : 'Process Maps'} Yet`}
            </h3>
            <p className="text-slate-500 max-w-md mx-auto">
              {searchTerm || filterCategory ? 'Try adjusting your search or filter.' : `Create your first ${activeTab === 'guidance' ? 'guidance item' : 'process map'} to get started.`}
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {currentItems.map((item: any) => {
              const isExpanded = expandedIds.has(item.id);
              const isGuidance = activeTab === 'guidance';

              return (
                <div key={item.id} className="bg-white rounded-3xl border border-slate-100 overflow-hidden">
                  {/* Row */}
                  <div
                    className={`flex items-center gap-4 px-5 py-4 cursor-pointer hover:bg-slate-50 transition-colors ${
                      item.is_active ? 'border-l-4 border-l-emerald-500' : 'border-l-4 border-l-slate-200'
                    }`}
                    onClick={() => toggleExpand(item.id)}
                  >
                    <ChevronDown size={16} className={`text-slate-400 flex-shrink-0 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />

                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${item.is_active ? 'bg-emerald-100' : 'bg-slate-100'}`}>
                      {isGuidance ? <FileText size={18} className={item.is_active ? 'text-emerald-600' : 'text-slate-400'} /> : <Map size={18} className={item.is_active ? 'text-emerald-600' : 'text-slate-400'} />}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-slate-900 truncate text-left">{item.title}</p>
                        {item.created_by_ai && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-violet-100 text-violet-700 border border-violet-200">✦ AI</span>
                        )}
                      </div>
                      {item.description && <p className="text-xs text-slate-500 truncate text-left">{item.description}</p>}
                    </div>

                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase border ${getCategoryColor(item.category)}`}>
                      {item.category}
                    </span>

                    <span className={`inline-flex items-center px-2 py-1 rounded-md font-bold text-[10px] ${item.is_active ? 'bg-emerald-100 text-emerald-800 border border-emerald-200' : 'bg-slate-100 text-slate-600 border border-slate-200'}`}>
                      {item.is_active ? 'Active' : 'Inactive'}
                    </span>

                    <span className="text-xs text-slate-400 flex-shrink-0">{new Date(item.updated_at).toLocaleDateString()}</span>

                    {/* Row actions — hidden when expanded */}
                    {!isExpanded && (
                      <div className="flex items-center gap-1 flex-shrink-0" onClick={e => e.stopPropagation()}>
                        <button
                          onClick={() => {
                            if (isGuidance) openModal({ component: GuidancePreviewModal, props: { guidance: item }, options: { size: 'large' } });
                            else openModal({ component: ProcessMapPreviewModal, props: { processMap: item }, options: { size: 'large' } });
                          }}
                          className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" title="Preview"
                        >
                          <Eye size={15} />
                        </button>
                        <button
                          onClick={() => {
                            if (isGuidance) openModal({ component: GuidanceModal, props: { onSubmit: handleSaveGuidance, selectedGuidance: item }, options: { size: 'large' } });
                            else openModal({ component: ProcessMapModal, props: { onSubmit: handleSaveProcessMap, selectedProcessMap: item }, options: { size: 'large' } });
                          }}
                          className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors" title="Edit"
                        >
                          <Edit size={15} />
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Expanded panel */}
                  {isExpanded && (
                    <div className="border-t border-slate-100 bg-slate-50/50">
                      <div className="ml-14 mr-5 my-3 space-y-3">
                        {/* Info cards */}
                        <div className="grid grid-cols-3 gap-3">
                          <div className="bg-white border border-slate-200 rounded-lg px-4 py-3">
                            <div className="flex items-center gap-2 text-xs text-slate-500 mb-1"><Tag size={12} /> Category</div>
                            <p className="text-sm font-semibold text-slate-800 capitalize">{item.category}</p>
                          </div>
                          <div className="bg-white border border-slate-200 rounded-lg px-4 py-3">
                            <div className="flex items-center gap-2 text-xs text-slate-500 mb-1"><Calendar size={12} /> Last Updated</div>
                            <p className="text-sm font-semibold text-slate-800">{new Date(item.updated_at).toLocaleDateString()}</p>
                          </div>
                          {isGuidance && item.framework_name && (
                            <div className="bg-white border border-slate-200 rounded-lg px-4 py-3">
                              <div className="flex items-center gap-2 text-xs text-slate-500 mb-1"><BookOpen size={12} /> Framework</div>
                              <p className="text-sm font-semibold text-slate-800">{item.framework_name}</p>
                            </div>
                          )}
                          {!isGuidance && (
                            <div className="bg-white border border-slate-200 rounded-lg px-4 py-3">
                              <div className="flex items-center gap-2 text-xs text-slate-500 mb-1"><Map size={12} /> Type</div>
                              <p className="text-sm font-semibold text-slate-800">Process Diagram</p>
                            </div>
                          )}
                        </div>

                        {/* Description */}
                        {item.description && (
                          <p className="text-sm text-slate-600 leading-relaxed">{item.description}</p>
                        )}

                        {/* Actions inside panel */}
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => {
                              if (isGuidance) openModal({ component: GuidancePreviewModal, props: { guidance: item }, options: { size: 'large' } });
                              else openModal({ component: ProcessMapPreviewModal, props: { processMap: item }, options: { size: 'large' } });
                            }}
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg border border-blue-200 transition-colors"
                          >
                            <Eye size={13} /> Preview
                          </button>
                          <button
                            onClick={() => {
                              if (isGuidance) openModal({ component: GuidanceModal, props: { onSubmit: handleSaveGuidance, selectedGuidance: item }, options: { size: 'large' } });
                              else openModal({ component: ProcessMapModal, props: { onSubmit: handleSaveProcessMap, selectedProcessMap: item }, options: { size: 'large' } });
                            }}
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-slate-700 bg-white hover:bg-slate-100 rounded-lg border border-slate-200 transition-colors"
                          >
                            <Edit size={13} /> Edit
                          </button>
                          <button
                            onClick={() => isGuidance ? handleDeleteGuidance(item.id) : handleDeleteProcessMap(item.id)}
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-red-700 bg-red-50 hover:bg-red-100 rounded-lg border border-red-200 transition-colors"
                          >
                            <Trash2 size={13} /> Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default GuidanceAndProcessMaps;
